import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { trpc } from '@/trpc/client';
import { Loader2, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';

const BookingSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const txRef = searchParams.get('tx_ref');
    const hasVerified = useRef(false);

    const verifyMutation = trpc.booking.verifyBookingPayment.useMutation();

    useEffect(() => {
        if (txRef && !hasVerified.current) {
            hasVerified.current = true;
            verifyMutation.mutate({ txRef });
        }
    }, [txRef, verifyMutation]);

    const jobId = verifyMutation.data?.status === 'success' ? (verifyMutation.data as any).booking?.jobId : null; // Backend returns { status: 'success', message: '...' } usually. We might need jobId if possible, but let's just offer "Go to Jobs" or "Go to Dashboard" if ID missing.

    // Actually, verification response in booking.ts returns { status: 'success', message: 'Job secured successfully!' }
    // It doesn't return the jobId explicitly in the return object yet.
    // I should probably redirect to something generic or ask user to go to dashboard.
    // Or just look at searchParams if I passed jobId? No I didn't.
    // Let's just say "Go to My Jobs".

    return (
        <div className="container max-w-md mx-auto py-24 px-4 animate-fade-in">
            <Card className="border-2">
                <CardHeader className="text-center">
                    <CardTitle className="text-2xl">Payment Status</CardTitle>
                    <CardDescription>Verifying your transaction with PayChangu...</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center justify-center space-y-6 min-h-[200px]">
                    {verifyMutation.isLoading && (
                        <div className="flex flex-col items-center space-y-4">
                            <Loader2 className="w-16 h-16 text-primary animate-spin" />
                            <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
                        </div>
                    )}

                    {verifyMutation.isSuccess && verifyMutation.data.status === 'success' && (
                        <div className="flex flex-col items-center space-y-4 text-center">
                            <CheckCircle className="w-20 h-20 text-green-500" />
                            <div className="space-y-2">
                                <h3 className="text-xl font-bold text-green-700">Payment Verified!</h3>
                                <p className="text-muted-foreground">Your booking has been secured successfully. You can now proceed with the job.</p>
                            </div>
                        </div>
                    )}

                    {verifyMutation.isSuccess && verifyMutation.data.status === 'pending' && (
                        <div className="flex flex-col items-center space-y-4 text-center">
                            <Loader2 className="w-16 h-16 text-yellow-500 animate-spin" />
                            <div className="space-y-2">
                                <h3 className="text-xl font-bold text-yellow-600">Payment Pending</h3>
                                <p className="text-muted-foreground">We are still waiting for confirmation from PayChangu. This might take a moment.</p>
                            </div>
                        </div>
                    )}

                    {(verifyMutation.isError || (verifyMutation.isSuccess && verifyMutation.data.status === 'error')) && (
                        <div className="flex flex-col items-center space-y-4 text-center">
                            <XCircle className="w-20 h-20 text-destructive" />
                            <div className="space-y-2">
                                <h3 className="text-xl font-bold text-destructive">Verification Failed</h3>
                                <p className="text-muted-foreground">
                                    {verifyMutation.error?.message || verifyMutation.data?.message || "There was an error verifying your payment."}
                                </p>
                                <p className="text-xs text-muted-foreground">Reference: {txRef}</p>
                            </div>
                        </div>
                    )}
                </CardContent>
                <CardFooter className="flex justify-center flex-col gap-2">
                    {verifyMutation.status === 'success' && verifyMutation.data.status === 'success' && (
                        <Button asChild className="w-full" size="lg">
                            <Link to="/dashboard/transporter">
                                Go to Transporter Dashboard <ArrowRight className="ml-2 w-4 h-4" />
                            </Link>
                        </Button>
                    )}
                    <Button variant="ghost" asChild className="w-full">
                        <Link to="/dashboard">Return to Dashboard</Link>
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
};

export default BookingSuccessPage;
